/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { AzureCsAccountStore } from 'src/core/stores/AzureCsAccountStore';
import {
    excludeAccountIfExists,
    getOrDefaultRegionFromResource,
    isAuthoringResource,
    isCognitiveServicesResource,
    isCustomTextResource,
    isPredictionResource,
    isPredictionResourceAllowedInAuthoringRegion,
    isTextAnalyticsResource
} from 'src/core/utils/csAccountUtils';
import { FeatureToggles } from 'src/core/utils/featureToggles';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { getPortalFromUrl } from 'src/core/utils/routingUtils';

const translatorResourceStrategy = (resource: AzureCsAccountStore) =>
    resource?.kind === 'TextTranslation' && (resource?.sku.code === 'S1' || resource?.sku.code === 'D3');

const languageStudioResourceStrategy = (resource: AzureCsAccountStore) => {
    const { subPath } = getPortalFromUrl();

    return subPath === 'customText'
        ? isCustomTextResource(resource)
        : isTextAnalyticsResource(resource) ||
              (subPath === 'root' && isCognitiveServicesResource(resource)) ||
              ((FeatureToggles.DocumentTranslationTool ||
                  FeatureToggles.DocumentTranslationTryOut ||
                  FeatureToggles.TextTranslationTryOut) &&
                  translatorResourceStrategy(resource));
};

const customTextResourceStrategy = (resource: AzureCsAccountStore) =>
    resource.kind === 'TextAnalytics' &&
    LuisConstants.LanguageStudio.AllowedSkuCodes.some(sku => sku === resource.sku.code) &&
    LuisConstants.CustomText.PrivatePreviewAllowedRegions.some(region => region === resource.region);

const luisStrategy = (resource: AzureCsAccountStore) => {
    if (isAuthoringResource(resource)) {
        return true;
    }

    const { hostname } = window.location;
    const isValidRegion =
        resource.region === 'westeurope' || (hostname === LuisConstants.Luis.StagingHostName && resource.region === 'westus2');

    return resource.kind === 'TextAnalytics' && isValidRegion && resource.sku.code === 'S';
};

const luisAuthoringStrategy = (resource: AzureCsAccountStore, currentGlobalCsAccount: AzureCsAccountStore) =>
    (isAuthoringResource(resource) || (resource.kind === 'TextAnalytics' && resource.sku.code === 'S')) &&
    getOrDefaultRegionFromResource(currentGlobalCsAccount) === resource.region;

const luisAppPredictionStrategy = (
    resource: AzureCsAccountStore,
    currentGlobalCsAccount: AzureCsAccountStore,
    assignedAzureAccounts: AzureCsAccountStore[]
) =>
    isPredictionResource(resource) &&
    excludeAccountIfExists(assignedAzureAccounts)(resource) &&
    isPredictionResourceAllowedInAuthoringRegion(getOrDefaultRegionFromResource(currentGlobalCsAccount))(resource);

export const AzureResourcesStrategies = {
    luisStrategy,
    luisAuthoringStrategy,
    luisAppPredictionStrategy,
    customTextResourceStrategy,
    translatorResourceStrategy,
    languageStudioResourceStrategy
};
